'use client';

import { type CSSProperties, useState } from 'react';

import { cn } from '../utils';

interface RemoteAvatarProps {
  /** Remote (firebase) avatar URL. Falls back to `fallback` when missing or broken. */
  src?: string | null;
  /** Initials / glyph shown when there is no image or the image fails to load. */
  fallback: React.ReactNode;
  /** Square size in pixels. */
  size: number;
  className?: string;
  style?: CSSProperties;
  alt?: string;
}

/**
 * Round avatar for a remote (firebase) image URL with a graceful fallback:
 * when the URL is absent or the image fails to load, it renders the `fallback`
 * (typically initials) instead of the browser's broken-image glyph.
 *
 * Uses a plain `<img>` (firebase URLs aren't in the next/image domain config).
 */
export function RemoteAvatar({
  src,
  fallback,
  size,
  className,
  style,
  alt = '',
}: RemoteAvatarProps) {
  const [failed, setFailed] = useState(false);
  const sizeStyle = { width: size, height: size, ...style };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt}
        style={sizeStyle}
        onError={() => setFailed(true)}
        className={cn(
          'bg-surface-1 shrink-0 rounded-full object-cover',
          className,
        )}
      />
    );
  }

  return (
    <span
      style={sizeStyle}
      className={cn(
        'bg-raise/10 text-content-1 flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        className,
      )}
    >
      {fallback}
    </span>
  );
}
